// Shell State Store (Zustand) — §12.2 / DR-09. The host owns this as the master
// context keeper across the Module Federation boundary.
//
// WHY ZUSTAND (not Redux): the store must be a singleton readable by workspace
// MFE remotes WITHOUT wrapping them in the host's React provider tree. Zustand
// stores are plain external stores read via a hook (useSyncExternalStore under
// the hood), so a remote can call useShellState() directly. A Redux <Provider>
// context does not cross the federation boundary cleanly. Zustand also drops the
// slice/reducer/inject boilerplate the v1 host (daxl-web-host) needed.
//
// Surfaces READ this store; they do NOT write to it. Mutations flow through the
// typed event bus (§12.3, §12.4). The setters below are used only by the host's
// own features (Auth, Navigation, ESA), which own the source-of-truth updates.

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';
import type {
  ApplicationId,
  Breadcrumb,
  CanvasId,
  CapabilityId,
  ModuleId,
  PermissionsManifest,
  PersonalizationContext,
  ShellState,
  SolutionId,
  Tenant,
  User,
  WorkspaceId,
} from '@growthaxl/web-foundation';

import type { ResolvingTabSpec } from '../../features/Esa/types';
import { clearSolutionsCatalog } from '../events/solutionsCatalogBridge';
import {
  RESOLVING_TAB_WINDOW_KEY,
  SHELL_CONTEXT_STORAGE_KEY,
  USER_THEME_STORAGE_KEY,
} from '../../constants/storageKeys';

export type EsaPanelMode = 'chat' | 'briefing' | 'thread-list' | 'inbox' | 'resolve';
export type EsaInboxSubTab = 'requests' | 'notifications';

/** Host-owned auth gate status (shared across all useAuth() call sites). */
export type AuthStatus = 'initializing' | 'authenticated' | 'unauthenticated';

export const PANEL_DEFAULT_RATIO = 0.30;

/** Module/solution-specific ESA panel width ratio (30% ESA / 70% canvas by default). */
export function resolveEsaPanelRatio(
  _activeModule: ModuleId | null,
  _activeSolution: SolutionId | null,
): number {
  return PANEL_DEFAULT_RATIO;
}

export interface ContentFrameSpec {
  type: string;
  spec: Record<string, unknown>;
}

interface ShellStore extends ShellState {
  // Host-internal setters (not part of the Surface Interface Contract).
  setAuth(user: User | null, tenant: Tenant | null, permissions: PermissionsManifest | null): void;
  setActiveWorkspace(workspaceId: WorkspaceId | null): void;
  setActiveCanvas(canvasId: CanvasId | null): void;
  setBreadcrumbs(breadcrumbs: Breadcrumb[]): void;
  setPersonalization(personalization: PersonalizationContext | null): void;
  setEsaPanelOpen(open: boolean): void;
  setEsaConversationId(id: string | null): void;
  setSolutionContext(solution: SolutionId | null, application?: ApplicationId | null): void;
  setModuleContext(module: ModuleId | null, application?: ApplicationId | null): void;
  setUserTheme(theme: 'light' | 'dark'): void;
  reset(): void;

  // Auth status is host-internal (not part of ShellState / remote contract).
  // Kept in the store so App, LoginSuccessPage, and Layout share one gate —
  // local useState in useAuth caused post-login bounce back to /login.
  authStatus: AuthStatus;
  setAuthStatus(status: AuthStatus): void;

  // ESA Panel host-internal extensions (not surfaced to remotes via useShellState).
  esaPanelMode: EsaPanelMode;
  /** ESA panel width as a ratio (0–1) of the viewport width. */
  esaPanelRatio: number;
  contentFrameSpec: ContentFrameSpec | null;
  /** Active solution capability tab (dashboard, results, …). */
  activeCapability: CapabilityId | null;
  /** Dynamic resolving tab spec — non-null when in resolve mode (DR-4). */
  resolvingTab: ResolvingTabSpec | null;
  /**
   * One-shot inbox sub-tab to open (toast “View in notifications”).
   * InboxView consumes and clears this.
   */
  esaInboxSubTab: EsaInboxSubTab | null;
  setEsaPanelMode(mode: EsaPanelMode): void;
  setEsaPanelRatio(ratio: number): void;
  setContentFrameSpec(spec: ContentFrameSpec | null): void;
  setActiveCapability(capability: CapabilityId | null): void;
  setResolvingTab(spec: ResolvingTabSpec | null): void;
  setEsaInboxSubTab(tab: EsaInboxSubTab | null): void;
}

const initialState: ShellState = {
  user: null,
  tenant: null,
  permissions: null,
  activeWorkspace: null,
  activeCanvas: null,
  breadcrumbs: [],
  personalization: null,
  esaPanelOpen: true,
  esaConversationId: null,
  activeSolution: null,
  activeModule: null,
  activeApplication: null,
};

export const useShellStore = create<ShellStore>()(
  persist(
    set => ({
      ...initialState,
      authStatus: 'initializing' as AuthStatus,
      esaPanelMode: 'briefing',
      esaPanelRatio: PANEL_DEFAULT_RATIO,
      contentFrameSpec: null,
      activeCapability: null,
      resolvingTab: null,
      esaInboxSubTab: null,

      setAuth: (user, tenant, permissions) => set({ user, tenant, permissions }),
      setAuthStatus: authStatus => set({ authStatus }),
      setActiveWorkspace: activeWorkspace => set({ activeWorkspace }),
      setActiveCanvas: activeCanvas => set({ activeCanvas }),
      setBreadcrumbs: breadcrumbs => set({ breadcrumbs }),
      setPersonalization: personalization => set({ personalization }),
      setEsaPanelOpen: esaPanelOpen => set({ esaPanelOpen }),
      setEsaConversationId: esaConversationId => set({ esaConversationId }),
      setSolutionContext: (activeSolution, activeApplication = null) =>
        set({ activeSolution, activeModule: null, activeApplication }),
      setModuleContext: (activeModule, activeApplication = null) =>
        set({ activeModule, activeSolution: null, activeApplication }),
      setUserTheme: (theme) => {
        if (typeof window !== 'undefined') {
          localStorage.setItem(USER_THEME_STORAGE_KEY, theme);
        }
        set(state => ({
          personalization: {
            ...(state.personalization ?? {
              locale: 'en',
              dateFormat: 'MM/DD/YYYY',
              currency: 'USD',
              timezone: 'UTC',
              recentEntities: [],
              preferredViews: {},
              featureFlags: {},
            }),
            theme,
          },
        }));
      },
      reset: () => {
        // Clear cross-bundle window snapshots so a post-logout canvas remount
        // can't resurrect stale data (resolvingTabBridge, solutionsCatalogBridge).
        if (typeof window !== 'undefined') {
          (window as unknown as Record<string, unknown>)[RESOLVING_TAB_WINDOW_KEY] = null;
          clearSolutionsCatalog();
          localStorage.removeItem(SHELL_CONTEXT_STORAGE_KEY);
        }
        set({
          ...initialState,
          authStatus: 'unauthenticated',
          esaPanelMode: 'briefing',
          esaPanelRatio: PANEL_DEFAULT_RATIO,
          contentFrameSpec: null,
          activeCapability: null,
          resolvingTab: null,
          esaInboxSubTab: null,
        });
      },
      setEsaPanelMode: esaPanelMode => set({ esaPanelMode }),
      setEsaPanelRatio: esaPanelRatio => set({ esaPanelRatio }),
      setContentFrameSpec: contentFrameSpec => set({ contentFrameSpec }),
      setActiveCapability: activeCapability => set({ activeCapability }),
      setResolvingTab: resolvingTab => set({ resolvingTab }),
      setEsaInboxSubTab: esaInboxSubTab => set({ esaInboxSubTab }),
    }),
    {
      name: SHELL_CONTEXT_STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      partialize: state => ({
        activeModule: state.activeModule,
        activeSolution: state.activeSolution,
        activeApplication: state.activeApplication,
        esaConversationId: state.esaConversationId,
        esaPanelRatio: state.esaPanelRatio,
      }),
      onRehydrateStorage: () => state => {
        if (!state || typeof window === 'undefined') return;

        try {
          const raw = localStorage.getItem(SHELL_CONTEXT_STORAGE_KEY);
          if (!raw) return;
          const persisted = JSON.parse(raw) as { state?: { esaPanelRatio?: number } };
          if (persisted.state?.esaPanelRatio === undefined) {
            useShellStore.setState({
              esaPanelRatio: resolveEsaPanelRatio(state.activeModule, state.activeSolution),
            });
          }
        } catch {
          // Ignore malformed persisted shell context.
        }
      },
    }
  )
);

/**
 * Read-only shell state hook — the Surface Interface Contract entry point
 * (§7.2 useShellState()). Exposed to remotes via Module Federation.
 */
export function useShellState(): ShellState {
  return useShellStore(
    useShallow(state => ({
      user: state.user,
      tenant: state.tenant,
      permissions: state.permissions,
      activeWorkspace: state.activeWorkspace,
      activeCanvas: state.activeCanvas,
      breadcrumbs: state.breadcrumbs,
      personalization: state.personalization,
      esaPanelOpen: state.esaPanelOpen,
      esaConversationId: state.esaConversationId,
      activeSolution: state.activeSolution,
      activeModule: state.activeModule,
      activeApplication: state.activeApplication,
    }))
  );
}

/** Vanilla (non-React) snapshot for imperative host code. */
export function getShellState(): ShellState {
  return useShellStore.getState();
}
